import { Box, Button, Center, ChakraProvider } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { ReactComponent as VenngageLogoIcon } from 'assets/icons/venngage_logo.svg';
import { STANDALONE_API_KEY } from 'constants/infograph';
import { GeneratorPaths, Routes } from 'constants/routes';
import { useEventListener } from 'hooks/useEventListener';
import {
  fetchE2APITokenByE2Id,
  fetchE2APITokenByInfographId,
  fetchE2APITokenByPrivateLinkId,
  fetchE2APITokenByPublicLinkId,
} from 'libs/e1api/design';
import { Mixpanel, RELOAD_ON_IMPORT_FAIL } from 'libs/third-party/Mixpanel';
import BlogPageLoader from 'modules/Blog/PageLoader';
import { PageLoader } from 'modules/common/components/Page/PageLoader';
import { ReactElement, Suspense, lazy, useEffect } from 'react';
import {
  LoaderFunction,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useNavigate,
} from 'react-router-dom';
import { isStandalone } from 'utils/environment';

import './app.css';
// We can move this to editor container if other view no need translation
import './i18n';
import { ErrorPage } from './modules/ErrorPage';
import { theme } from './theme';

const Editor = lazy(
  () =>
    import(
      /* webpackChunkName: "Editor" */
      /* webpackPrefetch: true */
      './modules/Editor/Editor'
    ),
);

const Export = lazy(
  () =>
    import(
      /* webpackChunkName: "Export" */
      './modules/Export'
    ),
);

const PrivateLinkView = lazy(
  () =>
    import(
      /* webpackChunkName: "PrivateLinkView" */
      /* webpackPrefetch: true */
      './modules/PrivateLinkView'
    ),
);

const PublicLinkView = lazy(
  () =>
    import(
      /* webpackChunkName: "PublicLinkView" */
      './modules/PublicLinkView'
    ),
);

const CardGenerator = lazy(
  () =>
    import(
      /* webpackChunkName: "CardGenerator" */
      './modules/DesignGenerator/CardGenerator'
    ),
);

const DesignGenerator = lazy(
  () =>
    import(
      /* webpackChunkName: "DesignAI" */
      './modules/DesignGenerator/DesignAI'
    ),
);

const DesignCreate = lazy(
  () =>
    import(
      /* webpackChunkName: "DesignCreate" */
      './modules/ComponentPage/CreateDesign'
    ),
);

const ComponentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ComponentPage" */
      './modules/ComponentPage'
    ),
);

const Admin = lazy(
  () =>
    import(
      /* webpackChunkName: "Admin" */
      './modules/Admin'
    ),
);

const BrandTemplatePreview = lazy(
  () =>
    import(
      /* webpackChunkName: "BrandTemplatePreview" */
      './modules/Onboarding/BrandTemplatePreview'
    ),
);

const HRTeams = lazy(
  () =>
    import(
      /* webpackChunkName: "HRTeams" */
      './modules/HRTeams'
    ),
);

const HRCalendar = lazy(
  () =>
    import(
      /* webpackChunkName: "HRCalendar" */
      './modules/HRCalendar'
    ),
);

const ResponsiveLinkView = lazy(
  () =>
    import(
      /* webpackChunkName: "ResponsiveLinkView" */
      './modules/ResponsiveLinkView'
    ),
);

const AdminGenerator = lazy(
  () =>
    import(
      /* webpackChunkName: "ResponsiveAdminGenerator" */
      './modules/AdminGenerator'
    ),
);

const Blog = lazy(() => import('./modules/Blog'));

const SuspenseEditor = (): ReactElement => (
  <Suspense fallback={<PageLoader />}>
    <Editor />
  </Suspense>
);

const SuspenseExport = (): ReactElement => (
  // Empty component fallback for export view
  <Suspense fallback={<></>}>
    <Export />
  </Suspense>
);

const SuspensePrivateLinkView = (): ReactElement => (
  // Empty component fallback for export view
  <Suspense fallback={<></>}>
    <PrivateLinkView />
  </Suspense>
);

const SuspensePublicLinkView = (): ReactElement => (
  // Empty component fallback for export view
  <Suspense fallback={<></>}>
    <PublicLinkView />
  </Suspense>
);

const SuspenseAdmin = (): ReactElement => (
  <Suspense fallback={'loading ...'}>
    <Admin />
  </Suspense>
);

const SuspenseCardGenerator = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <CardGenerator />
  </Suspense>
);

const SuspenseComponentPage = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <ComponentPage />
  </Suspense>
);

const SuspenseDesignGenerator = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <DesignGenerator />
  </Suspense>
);

const SuspenseDesignCreate = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <DesignCreate />
  </Suspense>
);

const SuspenseOnboarding = (): ReactElement => (
  <Suspense fallback={<></>}>
    <BrandTemplatePreview />
  </Suspense>
);

const SuspenseHRTeamsDashboard = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <HRTeams />
  </Suspense>
);

const SuspenseHRCalendar = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <HRCalendar />
  </Suspense>
);

const SuspenseResponsiveLinkView = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <ResponsiveLinkView />
  </Suspense>
);

const SuspenseBlogComponentPage = (): ReactElement => (
  <Suspense fallback={<BlogPageLoader />}>
    <Blog />
  </Suspense>
);

const SuspenseAIToolsComponentPage = (): ReactElement => (
  <Suspense fallback={<></>}>
    <ComponentPage />
  </Suspense>
);

const SuspenseAdminGenerator = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <AdminGenerator />
  </Suspense>
);

/**
 * Double-clicking on the logo navigates to admin page.
 * @constructor
 */
const JustLogo = (): ReactElement => {
  let navigate = useNavigate();

  function handleClick() {
    navigate('/admin');
  }

  return (
    <Center h='100vh'>
      <Box alignItems='center' display='flex' flexDirection='column'>
        <VenngageLogoIcon color='var(--vg-colors-brand-500)' />
        <Button size='sm' variant='action' mt={3} onClick={handleClick}>
          Admin
        </Button>
      </Box>
    </Center>
  );
};

const LoadingScreen = () => <Box>loading...</Box>;

/**
 * Fetch JWT token using e2 or e1 infograph id from API
 * If it is standalone, return the standalone API key
 * @param {LoaderParams}
 * @returns
 */
const tokenLoader: LoaderFunction = async ({ params }) => {
  const { id, privateLinkId, publicLinkId, e2Id } = params;

  if (isStandalone) return STANDALONE_API_KEY;

  if (privateLinkId) {
    return (await fetchE2APITokenByPrivateLinkId(privateLinkId))?.token;
  } else if (publicLinkId) {
    return (await fetchE2APITokenByPublicLinkId(publicLinkId))?.token;
  } else if (id) {
    return (await fetchE2APITokenByInfographId(id))?.token;
  } else if (e2Id) {
    return (await fetchE2APITokenByE2Id(e2Id))?.token;
  }
};

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

// Set the tokenLoader to use E2ClientProvider.
const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        errorElement={<ErrorPage />}
        path='design/create'
        element={<SuspenseDesignCreate />}
        loader={tokenLoader}
      />
      <Route errorElement={<ErrorPage />} path='design/:id' element={<SuspenseEditor />} loader={tokenLoader} />
      <Route
        errorElement={<ErrorPage />}
        path='pl/:privateLinkId'
        element={<SuspensePrivateLinkView />}
        loader={tokenLoader}
      />

      <Route
        errorElement={<ErrorPage />}
        path='ps/:privateLinkId'
        element={<SuspensePrivateLinkView />}
        loader={tokenLoader}
      />

      <Route
        errorElement={<ErrorPage />}
        path='pe/:publicLinkId'
        element={<SuspensePublicLinkView />}
        loader={tokenLoader}
      />
      <Route
        errorElement={<ErrorPage />}
        path='rl/:privateLinkId'
        element={<SuspenseResponsiveLinkView />}
        loader={tokenLoader}
      />
      <Route errorElement={<ErrorPage />} path='export/:id' element={<SuspenseExport />} loader={tokenLoader} />
      <Route
        errorElement={<ErrorPage />}
        path='infographStateView/:e2Id'
        element={<SuspenseExport />}
        loader={tokenLoader}
      />
      <Route path='export/:id/:pageId' element={<SuspenseExport />} loader={tokenLoader} />
      <Route errorElement={<ErrorPage />} path='onboarding/*' element={<SuspenseOnboarding />} />
      <Route errorElement={<ErrorPage />} path={GeneratorPaths.Card} element={<SuspenseCardGenerator />} />
      <Route errorElement={<ErrorPage />} path={GeneratorPaths.Infographic} element={<SuspenseDesignGenerator />} />
      <Route errorElement={<ErrorPage />} path={GeneratorPaths.Chart} element={<SuspenseDesignGenerator />} />
      <Route errorElement={<ErrorPage />} path={GeneratorPaths.Poster} element={<SuspenseDesignGenerator />} />
      <Route errorElement={<ErrorPage />} path={GeneratorPaths.Universal} element={<SuspenseDesignGenerator />} />
      <Route errorElement={<ErrorPage />} path={`${Routes.HR_TEAMS}/*`} element={<SuspenseHRTeamsDashboard />} />
      <Route errorElement={<ErrorPage />} path={'hr-teams/calendar'} element={<SuspenseHRCalendar />} />
      <Route errorElement={<ErrorPage />} path='ai-tools/*' element={<SuspenseAIToolsComponentPage />} />
      <Route errorElement={<ErrorPage />} path='features/*' element={<SuspenseComponentPage />} />
      <Route errorElement={<ErrorPage />} path='blog/*' element={<SuspenseBlogComponentPage />} />
      <Route errorElement={<ErrorPage />} path='vgadmin/AIGenerator' element={<SuspenseAdminGenerator />} />
      <Route path='admin' element={<SuspenseAdmin />} />
      <Route path='*' element={<JustLogo />} />
    </>,
  ),
);

/**
 * Handles Vite preload errors by reloading the page if not already done.
 * @see https://vitejs.dev/guide/build.html#load-error-handling
 */
const onVitePreloadError = () => {
  const shouldReload = sessionStorage.getItem('sessionReload');
  Mixpanel.track(RELOAD_ON_IMPORT_FAIL, { shouldReload: !shouldReload });
  if (!shouldReload) {
    window.location.reload();
    sessionStorage.setItem('sessionReload', 'done');
  }
};

export const App = (): ReactElement => {
  useEventListener('vite:preloadError', onVitePreloadError);
  useEffect(() => {
    /**
     * Dispatches a 'REACT_READY' event to notify parent applications that React
     * has finished its initial load. This allows parent apps to perform necessary actions
     * before React content is rendered (e.g. removing loading skeletons, initializing
     * external dependencies, etc).
     */
    window.dispatchEvent(new Event('REACT_READY'));
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <RouterProvider router={router} />
    </ChakraProvider>
  );
};

export default App;
